import { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NIP41_KIND_WHITELIST, NIP41_KIND_MIGRATION, NIP41_KIND_TIMESTAMP_PROOF } from '@/lib/nip41';

interface EventCardProps {
  event: any;
  title: string;
  status?: 'pending' | 'confirmed' | 'unknown';
}

export function EventCard({ event, title, status }: EventCardProps) {
  const [expanded, setExpanded] = useState(false);

  const kindLabel = {
    [NIP41_KIND_WHITELIST]: 'Whitelist',
    [NIP41_KIND_MIGRATION]: 'Migration',
    [NIP41_KIND_TIMESTAMP_PROOF]: 'Timestamp Proof',
  }[event.kind] || `Kind ${event.kind}`;

  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-950/20', border: 'border-amber-500/30' },
    confirmed: { icon: FileText, color: 'text-green-400', bg: 'bg-green-950/20', border: 'border-green-500/30' },
    unknown: { icon: FileText, color: 'text-slate-400', bg: 'bg-slate-900', border: 'border-slate-700' },
  };

  const config = statusConfig[status || 'unknown'];
  const Icon = config.icon;

  return (
    <div className={cn('rounded-xl border overflow-hidden', config.border, config.bg)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Icon className={cn('w-4 h-4', config.color)} />
          <div className="text-left">
            <h4 className="text-sm font-medium text-slate-200">{title}</h4>
            <p className={cn('text-xs', config.color)}>{kindLabel}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs font-mono text-slate-500">{event.id?.slice(0, 12)}...</span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/50 pt-3">
          <pre className="text-xs font-mono text-slate-400 overflow-auto max-h-64 bg-black/40 rounded-lg p-3">
            {JSON.stringify(event, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
